const FORMAT_SUMY =
  new Intl.NumberFormat(
    "sk-SK",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

function bezpecneCislo(hodnota) {
  const vysledok =
    Number(hodnota);

  return Number.isFinite(vysledok)
    ? vysledok
    : 0;
}

function formatujSumu(hodnota) {
  return `${
    FORMAT_SUMY.format(
      bezpecneCislo(hodnota)
    )
  } €`;
}

function formatujCisloPreCsv(
  hodnota
) {
  return bezpecneCislo(hodnota)
    .toFixed(2)
    .replace(".", ",");
}

function escapeCsv(hodnota) {
  const text =
    String(hodnota ?? "");

  return /[;"\r\n]/.test(text)
    ? `"${text.replace(
        /"/g,
        '""'
      )}"`
    : text;
}

function escapeHtml(hodnota) {
  return String(hodnota ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function zostavExportneData({
  riadky,
  rezim,
  zobrazitRealnuHodnotu,
  zobrazitPoplatky,
}) {
  const hlavicky = [
    rezim === "roky"
      ? "Rok"
      : "Mesiac",
    "Vklady (€)",
    "Zhodnotenie (€)",
  ];

  if (zobrazitPoplatky) {
    hlavicky.push(
      "Poplatky (€)",
      "Vplyv poplatkov (€)"
    );
  }

  hlavicky.push(
    "Hodnota investície (€)"
  );

  if (
    zobrazitRealnuHodnotu
  ) {
    hlavicky.push(
      "Reálna hodnota (€)"
    );
  }

  const data = riadky.map(
    (riadok) => {
      const hodnoty = [
        riadok.obdobie,
        riadok.vklady,
        riadok.zhodnotenie,
      ];

      if (zobrazitPoplatky) {
        hodnoty.push(
          riadok.poplatky,
          riadok.vplyvPoplatkov
        );
      }

      hodnoty.push(
        riadok.hodnotaInvesticie
      );

      if (
        zobrazitRealnuHodnotu
      ) {
        hodnoty.push(
          riadok.realnaHodnota
        );
      }

      return hodnoty;
    }
  );

  return {
    hlavicky,
    data,
  };
}

function stiahniSubor(
  nazov,
  mime,
  obsah
) {
  const blob = new Blob(
    [obsah],
    {
      type: mime,
    }
  );

  const url =
    URL.createObjectURL(blob);

  const odkaz =
    document.createElement("a");

  odkaz.href = url;
  odkaz.download = nazov;

  document.body.appendChild(
    odkaz
  );

  odkaz.click();
  odkaz.remove();

  URL.revokeObjectURL(url);
}

function vytvorNazovSuboru(
  variant,
  rezim,
  pripona
) {
  return (
    `vyvoj_investicie_` +
    `variant_${variant}_` +
    `${rezim}.${pripona}`
  );
}

export function exportujInvesticnyVyvojCsv({
  riadky,
  rezim,
  variant,
  zobrazitRealnuHodnotu,
  zobrazitPoplatky,
}) {
  const {
    hlavicky,
    data,
  } = zostavExportneData({
    riadky,
    rezim,
    zobrazitRealnuHodnotu,
    zobrazitPoplatky,
  });

  const obsah = [
    hlavicky
      .map(escapeCsv)
      .join(";"),

    ...data.map(
      (riadok) =>
        riadok
          .map(
            (
              hodnota,
              index
            ) =>
              escapeCsv(
                index === 0
                  ? hodnota
                  : formatujCisloPreCsv(
                      hodnota
                    )
              )
          )
          .join(";")
    ),
  ].join("\r\n");

  stiahniSubor(
    vytvorNazovSuboru(
      variant,
      rezim,
      "csv"
    ),
    "text/csv;charset=utf-8",
    `\uFEFF${obsah}`
  );
}

export function exportujInvesticnyVyvojPdf({
  riadky,
  rezim,
  variant,
  zobrazitRealnuHodnotu,
  zobrazitPoplatky,
  danZVynosu,
  konecnaHodnotaPoDani,
}) {
  const {
    hlavicky,
    data,
  } = zostavExportneData({
    riadky,
    rezim,
    zobrazitRealnuHodnotu,
    zobrazitPoplatky,
  });

  const okno = window.open(
    "",
    "_blank",
    "width=1200,height=800"
  );

  if (!okno) {
    return;
  }

  const hlavicka = hlavicky
    .map(
      (nazov) =>
        `<th>${
          escapeHtml(nazov)
        }</th>`
    )
    .join("");

  const telo = data
    .map(
      (
        riadok,
        indexRiadka
      ) => `
        <tr>
          ${
            riadok
              .map(
                (
                  hodnota,
                  index
                ) => `
                  <td>
                    ${
                      escapeHtml(
                        index === 0
                          ? `${
                              hodnota
                            }${
                              riadky[
                                indexRiadka
                              ]
                                ?.jeZaciatokDodatocnehoZhodnocovania
                                ? "*"
                                : ""
                            }`
                          : formatujSumu(
                              hodnota
                            )
                      )
                    }
                  </td>
                `
              )
              .join("")
          }
        </tr>
      `
    )
    .join("");

  const zobrazitDan =
    bezpecneCislo(
      danZVynosu
    ) > 0;

  const zobrazitPoznamkuDodatocnehoZhodnocovania =
    riadky.some(
      (riadok) =>
        riadok
          .jeZaciatokDodatocnehoZhodnocovania
    );

  const zobrazitSuhrn =
    zobrazitDan ||
    zobrazitPoznamkuDodatocnehoZhodnocovania;

  okno.document.write(`
    <!doctype html>
    <html lang="sk">
      <head>
        <meta charset="utf-8" />
        <title>
          Vývoj investície
        </title>

        <style>
          @page {
            size: A4 landscape;
            margin: 12mm;
          }

          body {
            margin: 0;
            color: #111;
            font-family:
              system-ui,
              -apple-system,
              "Segoe UI",
              sans-serif;
            font-size: 10px;
          }

          h1 {
            margin: 0 0 4px;
            font-size: 17px;
          }

          p {
            margin: 0 0 10px;
            color: #555;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          thead {
            display: table-header-group;
          }

          tr {
            break-inside: avoid;
          }

          th,
          td {
            border: 1px solid #d8d8d8;
            padding: 4px 6px;
            text-align: right;
          }

          th {
            background: #f2f2f2;
            font-weight: 700;
          }

          th:first-child,
          td:first-child {
            text-align: left;
          }

          .summary {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            margin-top: 10px;
            font-size: 11px;
          }

          .summary strong {
            color: #111;
          }

          .summary__note {
            font-weight: 700;
          }

          .summary__right {
            display: flex;
            gap: 20px;
            margin-left: auto;
          }
        </style>
      </head>

      <body>
        <h1>
          Vývoj investície –
          Variant ${
            escapeHtml(variant)
          }
        </h1>

        <p>
          ${
            rezim === "roky"
              ? "Ročný"
              : "Mesačný"
          }
          prehľad
        </p>

        <table>
          <thead>
            <tr>
              ${hlavicka}
            </tr>
          </thead>

          <tbody>
            ${telo}
          </tbody>
        </table>

        ${
          zobrazitSuhrn
            ? `
              <div class="summary">
                ${
                  zobrazitPoznamkuDodatocnehoZhodnocovania
                    ? `
                      <strong class="summary__note">
                        *Dodatočné zhodnocovanie bez vkladov
                      </strong>
                    `
                    : ""
                }

                ${
                  zobrazitDan
                    ? `
                      <div class="summary__right">
                        <span>
                          Odhadovaná daň pri ukončení:
                          <strong>
                            ${
                              escapeHtml(
                                formatujSumu(
                                  danZVynosu
                                )
                              )
                            }
                          </strong>
                        </span>

                        <span>
                          Konečná hodnota po zdanení:
                          <strong>
                            ${
                              escapeHtml(
                                formatujSumu(
                                  konecnaHodnotaPoDani
                                )
                              )
                            }
                          </strong>
                        </span>
                      </div>
                    `
                    : ""
                }
              </div>
            `
            : ""
        }
      </body>
    </html>
  `);

  okno.document.close();
  okno.focus();
  okno.print();
}